import React, { useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { position, PositionProps } from "styled-system";
import { Typography, Box, ResponsiveLayout } from "@bca/ui-library";

import { Image } from "../vehicleDetails/vehicle-details-dto.type";
import LeftArrowSvg from "./Icons/LeftArrowSVG";
import RightArrowSvg from "./Icons/RightArrowSVG";
import pushToDigitalData from "../analytics/pushToDigitalData";
import getFilteredImagesByType from "../helpers/getFilteredImagesByType";

import { useWindowOffset } from "./useWindowOffset.hook";
import { useStateRef } from "../hooks/customHooks";
import { shallowEqual, useSelector } from "react-redux";
import { StoreType } from "../redux/StoreType";
import { GET_VENDOR_IMPERSONATION_STATE } from "../redux/slices/impersonationSlice";
import { GET_USER_STATE } from "../redux/slices/userSlice";

const THUMBNAIL_MARGIN = 10;
const CONTAINER_MARGIN = 20;

interface ArrowProps extends PositionProps {
  children: React.ReactNode;
  onClick: () => void;
}
interface ThumbnailsProps extends PositionProps {
  activeImageIndex: number;
  title: string;
  images: Image[];
  setActiveImageIndex: (index: number) => void;
  hasImageFullWidth?: boolean;
}

const ImagesContainer = styled(Box)<PositionProps>`
  transition: left 0.5s;
  ${position}
`;

const ImageContainer = styled(Box)<PositionProps>`
  border-radius: 6px;
  ${position}
`;

const Thumbnail = styled.img<{ src: string }>`
  width: 100%;
  height: auto;
`;

const ImageOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  opacity: 0.55;
`;

const LeftArrow = styled.button<ArrowProps>`
  position: absolute;
  top: 0;
  width: 40px;
  height: 100%;
  padding: 15px;
  z-index: 2;
  border: none;
  background: transparent;
  background-image: linear-gradient(to left, transparent, #070b16);
  outline: none;
  left: -20px;
  @media screen and (min-width: 770px) {
    left: -60px;
  }
`;

const RightArrow = styled.button<ArrowProps>`
  ${position}
  position: absolute;
  top: 0;
  width: 40px;
  height: 100%;
  padding: 15px;
  z-index: 2;
  border: none;
  background: transparent;
  background-image: linear-gradient(to right, transparent, #070b16);
  outline: none;
`;

const labelCss = `
  color: white;
  font-weight: 100;
  z-index: 1;
`;

const ImagesWrapper = styled(Box)<PositionProps>`
  ${position}
`;

const pushThumbnailToAnalytics = (
  currentImage: Image,
  allImages: Image[],
  activeImageIndex: number,
  hasImageFullWidth: boolean,
  isVendorAdmin: boolean,
  isVendorImpersonationSet: boolean
) => {
  const wantedImage = currentImage ?? allImages[activeImageIndex - 1];
  const wantedImageType = wantedImage?.imageType.toLowerCase();

  const filteredImages = getFilteredImagesByType(
    allImages,
    wantedImageType === "damage" ? "damage" : "beauty"
  );
  const indexOfWantedImagePerCategory = filteredImages.indexOf(wantedImage) + 1;
  if ((isVendorAdmin && isVendorImpersonationSet) || isVendorAdmin === false) {
    pushToDigitalData({
      event: {
        name: "click",
        category: "button",
        label: `Image|${activeImageIndex}`,
        action: "thumbnail",
        section: hasImageFullWidth ? "gallery" : "in-page",
      },
      product: {
        image: {
          imageType:
            wantedImage?.imageType.toLowerCase() === "damage"
              ? "damage"
              : "beauty",
          imageNum: indexOfWantedImagePerCategory,
          imagePlacementNum: activeImageIndex,
        },
      },
    });
  }
};

const Thumbnails: React.FunctionComponent<ThumbnailsProps> = ({
  activeImageIndex,
  images,
  title,
  setActiveImageIndex,
  hasImageFullWidth,
  ...rest
}) => {
  const [totalWidth, setTotalWidth, totalWidthRef] = useStateRef(10000);
  const [, setFirstImageInViewport, firstImageInViewportRef] = useStateRef(0);
  const [offsetLeft, setOffsetLeft, offsetLeftRef] = useStateRef(0);
  const normalizedImageIndex =
    images.length === 1 ? activeImageIndex : activeImageIndex - 2;
  const imagesViewport = getImagesViewport();
  const showLeftArrow = offsetLeft < 0;
  const showRightArrow =
    typeof window !== "undefined"
      ? offsetLeft + totalWidth > imagesViewport
      : false;

  const startX = useRef(0);
  const direction = useRef(0);
  const [computeAndSetOffset, windowOffsetRef] = useWindowOffset();

  const handleResize = useCallback(() => {
    updateTotalWidth();
    setFirstImageInViewport(0);
    setOffsetLeft(0);
  }, []);

  const {
    user: { isVendorAdmin },
  }: StoreType = useSelector(GET_USER_STATE, shallowEqual);
  const {
    impersonation: { isVendorImpersonationSet },
  }: StoreType = useSelector(GET_VENDOR_IMPERSONATION_STATE, shallowEqual);

  useEffect(() => {
    computeAndSetOffset();
  }, [computeAndSetOffset]);

  useEffect(() => {
    updateTotalWidth();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const updateTotalWidth = useCallback(() => {
    const numberOfImages = images.length;
    const thumbnailWidth = getThumbnailWidth();
    const width =
      numberOfImages === 1
        ? window.innerWidth
        : numberOfImages * (thumbnailWidth + THUMBNAIL_MARGIN) +
          CONTAINER_MARGIN;
    setTotalWidth(width);
  }, [images, setTotalWidth]);

  const handleRightArrow = useCallback(() => {
    const thumbnailWidth = getThumbnailWidth();
    const imagesViewport = getImagesViewport();
    const imagesInViewport = Math.floor(
      imagesViewport / (thumbnailWidth + THUMBNAIL_MARGIN)
    );
    const widthOutsideTheViewport =
      totalWidthRef.current - imagesViewport + offsetLeftRef.current;
    const widthOfImagesOutsideViewport =
      totalWidthRef.current -
      imagesInViewport * (thumbnailWidth + THUMBNAIL_MARGIN) +
      offsetLeftRef.current;

    if (widthOfImagesOutsideViewport < imagesViewport) {
      const imagesOutsideOfViewport = Math.ceil(
        widthOutsideTheViewport / (thumbnailWidth + THUMBNAIL_MARGIN)
      );
      const firstImage =
        firstImageInViewportRef.current + imagesOutsideOfViewport;
      setFirstImageInViewport(firstImage);
      setOffsetLeft(
        offsetLeftRef.current - widthOutsideTheViewport - CONTAINER_MARGIN
      );
      return;
    }

    const firstImage = firstImageInViewportRef.current + imagesInViewport;
    setFirstImageInViewport(firstImage);
    setOffsetLeft(-firstImage * (thumbnailWidth + THUMBNAIL_MARGIN));
  }, []);

  const handleLeftArrow = useCallback(() => {
    const thumbnailWidth = getThumbnailWidth();
    const imagesViewport = getImagesViewport();
    const numberOfImagesInViewport = Math.floor(
      imagesViewport / (thumbnailWidth + THUMBNAIL_MARGIN)
    );

    if (offsetLeftRef.current > -imagesViewport) {
      setFirstImageInViewport(0);
      setOffsetLeft(0);
      return;
    }

    const firstImage =
      firstImageInViewportRef.current - numberOfImagesInViewport > 0
        ? firstImageInViewportRef.current - numberOfImagesInViewport
        : 0;
    setFirstImageInViewport(firstImage);
    setOffsetLeft(-firstImage * (thumbnailWidth + THUMBNAIL_MARGIN));
  }, []);

  const handleTouchStart = useCallback((evt) => {
    startX.current = evt.touches[0].pageX;
    direction.current = 0;
  }, []);

  const handleTouchMove = useCallback((evt) => {
    if (startX.current < evt.touches[0].pageX) {
      direction.current = -1;
      return;
    }
    if (startX.current > evt.touches[0].pageX) {
      direction.current = 1;
      return;
    }

    direction.current = 0;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (direction.current < 0) {
      handleLeftArrow();
      return;
    }
    const viewportIsNotFilled =
      offsetLeft + totalWidthRef.current <= imagesViewport;
    if (direction.current === 0 || viewportIsNotFilled) {
      return;
    }

    handleRightArrow();
  }, []);

  const isActiveImage = useCallback(
    (index) => {
      if (normalizedImageIndex > images.length - 1) {
        return index === 0;
      }

      if (normalizedImageIndex < 0) {
        return index === images.length - 1;
      }

      return index === normalizedImageIndex;
    },
    [images, normalizedImageIndex]
  );

  return (
    <ResponsiveLayout
      display="flex"
      flexDirection="column"
      overflow="hidden"
      mt={[-69, -84, -100]}
      data-testid="thumbnails"
      ml={
        typeof window !== "undefined" && window?.innerWidth > 1440
          ? "calc((100vw - 1440px)/2)"
          : "0px"
      }
      width={totalWidth}
      css={`
        box-sizing: content-box;
      `}
      {...rest}
    >
      <Typography
        css={labelCss}
        variant="caption"
      >{`${title} (${images.length})`}</Typography>
      <ImagesWrapper
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        position="relative"
        width={["calc(100% - 40px)", "100%"]}
        height={[45, 60]}
      >
        {showLeftArrow && (
          <LeftArrow
            data-en="click"
            data-ec="button"
            data-ea="scroll-sml"
            data-es={hasImageFullWidth ? "gallery" : "in-page"}
            data-testid="left-arrow"
            onClick={handleLeftArrow}
          >
            <LeftArrowSvg />
          </LeftArrow>
        )}
        <ImagesContainer
          display="flex"
          alignItems="center"
          data-testid="images-container"
          position="relative"
          left={offsetLeftRef.current}
        >
          {images.map((image, index) => {
            const currentActiveImageIndex = isActiveImage(index);
            const normalizedCurrentIndex =
              images.length === 1 ? index : index + 2;
            return (
              <ImageContainer
                key={image.imageURI}
                position="relative"
                overflow="hidden"
                width={currentActiveImageIndex ? [80, 110] : [70, 100]}
                height={currentActiveImageIndex ? [45, 60] : [40, 56]}
                m={currentActiveImageIndex ? 0 : `0 ${THUMBNAIL_MARGIN / 2}px`}
                onClick={() => {
                  setActiveImageIndex(normalizedCurrentIndex);
                  pushThumbnailToAnalytics(
                    image,
                    images,
                    normalizedCurrentIndex - 1,
                    hasImageFullWidth,
                    isVendorAdmin,
                    isVendorImpersonationSet
                  );
                }}
              >
                <Thumbnail src={image.imageURI} />
                {!currentActiveImageIndex && (
                  <ImageOverlay
                    width="100%"
                    height="100%"
                    backgroundColor="black"
                  />
                )}
              </ImageContainer>
            );
          })}
        </ImagesContainer>
        {showRightArrow && images && images.length !== 1 && (
          <RightArrow
            data-en="click"
            data-ec="button"
            data-ea="scroll-sml"
            data-es={hasImageFullWidth ? "gallery" : "in-page"}
            data-testid="right-arrow"
            onClick={handleRightArrow}
            left={{
              mobile: "calc(100vw - 60px)",
              tablet: "calc(100vw - 60px)",
              desktop: "calc(100vw - 100px)",
            }}
          >
            <RightArrowSvg />
          </RightArrow>
        )}
      </ImagesWrapper>
    </ResponsiveLayout>
  );
};

const getThumbnailWidth = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  if (window.innerWidth < 640) {
    return 70;
  }

  return 100;
};

const getImagesViewport = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  return window.innerWidth < 1440 ? window.innerWidth - 60 : 1440;
};

export default Thumbnails;
